import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export const SITE_NAME = 'Youth Connekt Cameroon';
export const SITE_ORIGIN = 'https://youthconnektcameroon.org';
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/android-chrome-512x512.png`;

export interface PageSeoData {
  /** Sans le suffixe " | Youth Connekt Cameroon" — ajouté automatiquement. */
  title: string;
  description: string;
  /** URL absolue ; par défaut le mark YCC (DEFAULT_OG_IMAGE). */
  image?: string;
  /** Chemin relatif (ex. "/events/mon-evenement") utilisé pour og:url et le lien canonique. */
  path: string;
  type?: 'website' | 'article';
}

/**
 * Titre/description/canonique + Open Graph/Twitter Card, posés côté serveur lors du rendu SSR
 * (voir app.routes.server.ts) donc visibles par les robots et les aperçus de partage sans exécuter
 * de JS. Les pages statiques passent par `data.seo` sur la route (voir public-site.routes.ts + le
 * listener central dans App) ; les pages de détail (contenu chargé en HTTP) appellent `set()`
 * elles-mêmes une fois la donnée réelle disponible.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  set(data: PageSeoData): void {
    const fullTitle = `${data.title} | ${SITE_NAME}`;
    const url = `${SITE_ORIGIN}${data.path}`;
    const image = data.image ?? DEFAULT_OG_IMAGE;

    this.title.setTitle(fullTitle);
    this.meta.updateTag({ name: 'description', content: data.description });
    this.meta.updateTag({ property: 'og:site_name', content: SITE_NAME });
    this.meta.updateTag({ property: 'og:type', content: data.type ?? 'website' });
    this.meta.updateTag({ property: 'og:title', content: fullTitle });
    this.meta.updateTag({ property: 'og:description', content: data.description });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: fullTitle });
    this.meta.updateTag({ name: 'twitter:description', content: data.description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.setCanonical(url);
  }

  /** Injecte (ou remplace) un bloc JSON-LD identifié par `id`, pour ne jamais en accumuler plusieurs au fil des navigations. */
  setJsonLd(data: object, id: string): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.setAttribute('type', 'application/ld+json');
    script.textContent = JSON.stringify(data);
    this.document.head.appendChild(script);
  }

  removeJsonLd(id: string): void {
    this.document.getElementById(id)?.remove();
  }

  private setCanonical(url: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
